import { Fragment, useMemo } from "react"
import { useField, useFormikContext } from "formik"
import { Divider } from "@mui/material"

import { FetchProductsForUserResponse, UserDetails } from "../../../../../../modules/nonAdminUser/nonAdminUserApi"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { queryClient } from "../../../../../../core/api/queryClient"
import { ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"

import { DeliveryOrderLine, DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { MeetingServicesPlaceholder } from "./MeetingServicesPlaceholder"
import { CreateOrderDialogMode, MeetingServicePriceListState } from "../../../createOrderTypes"
import { MeetingServiceProduct } from "./MeetingServiceProduct"
import { ProductUpdateQuantityProps } from "./MeetingServices"
import { OrderSummaryItemBox } from "./OrderSummaryItemBox"
import { filterShops } from "../../shop/helpers"

type Props = {
    serialNumber: number
    deliveryTime: string
    orderLines: DeliveryOrderLine[]
    deliveryDate: string | null
    isRefundOrder: boolean
    onUpdateQuantity: (props: ProductUpdateQuantityProps) => void
    isEditable: boolean
    userId: number
    mode?: CreateOrderDialogMode
}

export const MeetingServiceDelivery = ({
    serialNumber,
    deliveryTime,
    orderLines,
    deliveryDate,
    isRefundOrder,
    onUpdateQuantity,
    isEditable,
    userId,
    mode,
}: Props) => {
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()
    const dateTimeFactory = useDateTime()

    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const priceList: MeetingServicePriceListState["item"] = priceListState.item
    const [, deliveriesMeta] = useField(DynamicConfigKey.MULTIPLE_DELIVERIES)

    if (!priceList) return null

    const products: FetchProductsForUserResponse | undefined = queryClient.getQueryData([
        UserDetails.USER_PRODUCTS_LIST,
        {
            userId,
            merchantId: priceList.merchantId,
            priceListId: priceList.priceListId,
            deliveryDate: deliveryDate ?? dateTimeFactory.toDate(dateTimeFactory.now()),
            ...(mode === "edit" ? { mode } : {}),
        },
    ])

    const { filteredShops, catalogProducts } = useMemo(() => {
        const list = filterShops(false, undefined, products?.productGroups)
        const ids = new Set<number>()
        const catalog = new Map<number, ProductsTableItem>()
        for (const group of list || []) {
            for (const p of group.products || []) {
                if (typeof p.id === "number") {
                    ids.add(p.id)
                    catalog.set(p.id, p)
                }
            }
        }
        return { filteredShops: ids, catalogProducts: catalog }
    }, [products])

    const formattedDate = deliveryDate && dateTimeFactory.fromISO(deliveryDate).toFormat("d MMMM")
    const time = dateTimeFactory.fromHourMinute(deliveryTime).toFormat("t")

    const hasDeliveriesError = submitCount > 0 && typeof deliveriesMeta.error === "string"

    return (
        <OrderSummaryItemBox
            title={
                <LocalizedStrict
                    id="create-order-order-summary-meeting-services-delivery-time"
                    vars={{ number: serialNumber }}
                >
                    <>Delivery time:</>
                </LocalizedStrict>
            }
            titleDetails={`${formattedDate}, ${time}`}
        >
            {orderLines.length > 0 ? (
                orderLines.map((p, index) => (
                    <Fragment key={`${deliveryTime}-${p.id}`}>
                        {!!index && <Divider flexItem />}
                        <MeetingServiceProduct
                            product={p}
                            catalogProduct={typeof p.id === "number" ? catalogProducts.get(p.id) : undefined}
                            catalogProducts={catalogProducts}
                            deliveryTime={deliveryTime}
                            isRefundOrder={isRefundOrder}
                            onUpdateQuantity={onUpdateQuantity}
                            isEditable={isEditable}
                            availableProductIds={filteredShops}
                        />
                    </Fragment>
                ))
            ) : (
                <MeetingServicesPlaceholder isError={hasDeliveriesError} />
            )}
        </OrderSummaryItemBox>
    )
}
